/**
 * Phase 1 Variation Orders — eligibility, numbering, statuses.
 * Extensible: eligibility is D-Tools JSON only today; schema/composition are source-agnostic.
 */

import { isDtoolsJsonProposalSourceValue } from "./proposalSources";
import { isProposalApprovedOrWon } from "./proposalReviseWorkspace";

export const VO_STATUSES = ["draft", "sent", "approved", "rejected", "cancelled"] as const;
export type VariationOrderStatus = (typeof VO_STATUSES)[number];

export const VO_CHANGE_TYPES = ["added", "removed", "quantity_changed"] as const;
export type VariationOrderChangeType = (typeof VO_CHANGE_TYPES)[number];

/** Conceptual change types including non-line amendments (system overview). */
export {
  VO_SYSTEM_OVERVIEW_CHANGE_TYPE,
  type VoSystemOverviewChange,
  type VoSystemOverviewChangeType,
} from "./variationOrderSystemOverview";

export const VO_AUDIT_EVENTS = [
  "created",
  "edited",
  "sent",
  "viewed",
  "approved",
  "rejected",
  "cancelled",
  "withdrawn",
  "reason_changed",
  "product_replaced",
  "replacement_reason_changed",
  "room_created",
  "supporting_components_added",
  "discount_changed",
] as const;
export type VariationOrderAuditEvent = (typeof VO_AUDIT_EVENTS)[number];

/** Deal timeline event types bridged from VO lifecycle. */
export const VO_TIMELINE_EVENT_TYPES = [
  "variation_order.created",
  "variation_order.updated",
  "variation_order.sent",
  "variation_order.viewed",
  "variation_order.approved",
  "variation_order.rejected",
  "variation_order.cancelled",
  "variation_order.withdrawn",
] as const;
export type VariationOrderTimelineEventType = (typeof VO_TIMELINE_EVENT_TYPES)[number];

export function voAuditToTimelineEventType(
  audit: VariationOrderAuditEvent | string,
): VariationOrderTimelineEventType | null {
  switch (String(audit)) {
    case "created":
      return "variation_order.created";
    case "edited":
      return "variation_order.updated";
    case "sent":
      return "variation_order.sent";
    case "viewed":
      return "variation_order.viewed";
    case "approved":
      return "variation_order.approved";
    case "rejected":
      return "variation_order.rejected";
    case "cancelled":
      return "variation_order.cancelled";
    case "withdrawn":
      return "variation_order.withdrawn";
    default:
      return null;
  }
}

export function voTimelineEventLabel(eventType: VariationOrderTimelineEventType | string): string {
  switch (eventType) {
    case "variation_order.created":
      return "Variation Order created";
    case "variation_order.updated":
      return "Variation Order updated";
    case "variation_order.sent":
      return "Variation Order sent";
    case "variation_order.viewed":
      return "Variation Order viewed by customer";
    case "variation_order.approved":
      return "Variation Order approved";
    case "variation_order.rejected":
      return "Variation Order rejected";
    case "variation_order.cancelled":
      return "Variation Order cancelled";
    case "variation_order.withdrawn":
      return "Variation Order withdrawn to draft";
    default:
      return String(eventType).replace(/[._]/g, " ");
  }
}

export function formatVoNumber(sequence: number): string {
  const n = Math.max(1, Math.floor(sequence));
  return `VO-${String(n).padStart(3, "0")}`;
}

export function parseVoSequence(voNumber: string): number | null {
  const m = /^VO-(\d+)$/i.exec(String(voNumber ?? "").trim());
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Phase 1: approved D-Tools JSON proposals only.
 * Classic project_proposal can be added later without changing VO tables.
 */
export function canCreateVariationOrder(header: {
  proposalSource?: string | null;
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}): boolean {
  if (header.cancelledAt) return false;
  if (!isDtoolsJsonProposalSourceValue(header.proposalSource)) return false;
  return isProposalApprovedOrWon(header);
}

/** Persist edits only while Draft. Sent must be withdrawn to Draft first. */
export function canEditVariationOrderDraft(status: string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "draft";
}

/** Salespeople may open Edit for Draft or Sent (Awaiting Approval). Approved is immutable. */
export function canEditVariationOrder(status: string | null | undefined): boolean {
  const s = String(status ?? "").toLowerCase();
  return s === "draft" || s === "sent";
}

/** Salespeople may soft-cancel Draft or Sent. Approved cannot be cancelled. */
export function canCancelVariationOrder(status: string | null | undefined): boolean {
  const s = String(status ?? "").toLowerCase();
  return s === "draft" || s === "sent";
}

/** Sent → Draft withdrawal (invalidate customer approval link). */
export function canWithdrawSentVariationOrder(status: string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "sent";
}

/** Approved VOs are permanent contractual history — no edit/cancel/delete. */
export function isVariationOrderImmutable(status: string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "approved";
}

export function canSendVariationOrder(status: string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "draft";
}

export function canCustomerActOnVariationOrder(status: string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "sent";
}

/** Stable ISO document version for VO approve concurrency (client ↔ server). */
export function voDocumentVersionFromUpdatedAt(updatedAt: unknown): string {
  if (updatedAt instanceof Date && !Number.isNaN(updatedAt.getTime())) {
    return updatedAt.toISOString();
  }
  const raw = String(updatedAt ?? "").trim();
  if (!raw) return "";
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString();
}

/** Customer-facing status when link is no longer actionable. */
export function variationOrderInactiveCustomerMessage(status: string | null | undefined): string | null {
  const s = String(status ?? "").toLowerCase();
  if (s === "draft") {
    return "This Variation Order has been updated by your consultant. The previous approval link is no longer valid. Please wait for a new link.";
  }
  if (s === "cancelled") {
    return "This Variation Order has been cancelled and is no longer available for approval.";
  }
  if (s === "approved") {
    return "This Variation Order has already been approved.";
  }
  if (s === "rejected") {
    return "This Variation Order was rejected.";
  }
  return null;
}

/** Display label for staff UI — Sent shows as Awaiting Approval. */
export function variationOrderStatusLabel(status: string | null | undefined): string {
  const s = String(status ?? "").toLowerCase();
  if (s === "sent") return "Awaiting Approval";
  if (!s) return "—";
  return s.replace(/_/g, " ");
}

/** Active = draft or sent (awaiting customer). Only one may exist per Commercial Head. */
export function isActiveVariationOrderStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "").toLowerCase();
  return s === "draft" || s === "sent";
}

export function awaitingApprovalVoBlockedMessage(voNumber: string): string {
  const n = String(voNumber ?? "").trim() || "This Variation Order";
  return `${n} is already awaiting approval. Please continue editing that Variation Order or cancel it before creating another.`;
}

export type SingleActiveVoDecision =
  | { action: "create" }
  | { action: "reopen"; voId: number; voNumber: string }
  | { action: "block"; voId: number; voNumber: string; message: string };

/**
 * Hard rule: at most one active VO (draft|sent).
 * Draft → reopen. Sent → block with clear error (never create VO-00N+1).
 */
export function decideSingleActiveVoCreate(
  existing: { id: number; voNumber: string; status: string } | null | undefined,
): SingleActiveVoDecision {
  if (!existing) return { action: "create" };
  const status = String(existing.status ?? "").toLowerCase();
  if (status === "draft") {
    return { action: "reopen", voId: existing.id, voNumber: existing.voNumber };
  }
  if (status === "sent") {
    return {
      action: "block",
      voId: existing.id,
      voNumber: existing.voNumber,
      message: awaitingApprovalVoBlockedMessage(existing.voNumber),
    };
  }
  return { action: "create" };
}

/**
 * Pure chaining helper: next VO base = last approved revised value, else master header total.
 * VO-002 must start from Commercial Head after VO-001, never the original frozen proposal alone.
 */
export function resolveContractValueBeforeVoFromApproved(input: {
  masterTotal: number;
  approvedVos: Array<{ revisedContractValue?: unknown; variationAmount?: unknown }>;
}): number {
  const master = roundVoMoney(Number(input.masterTotal ?? 0) || 0);
  const approved = input.approvedVos ?? [];
  if (!approved.length) return master;
  const last = approved[approved.length - 1]!;
  const lastRevised = Number(last.revisedContractValue);
  if (Number.isFinite(lastRevised)) return roundVoMoney(lastRevised);
  const summed = approved.reduce((s, v) => s + (Number(v.variationAmount) || 0), 0);
  return roundVoMoney(master + summed);
}

/** Chronological permanent history: oldest first (VO-001 → VO-00N). Never drop cancelled. */
export function sortVariationOrdersChronological<T extends { id: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.id - b.id);
}

export function roundVoMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * VO financial snapshot:
 * Original = contract value immediately before this VO
 * Revised = Original + Variation (net)
 *
 * Never derive Revised from raw workspace line totals (those omit adjustments /
 * can diverge from the approved proposal header total).
 */
export function computeVoFinancialSnapshot(input: {
  contractValueBeforeVo: number;
  variationAmount: number;
}): {
  originalContractValue: number;
  variationAmount: number;
  revisedContractValue: number;
} {
  const originalContractValue = roundVoMoney(input.contractValueBeforeVo);
  const variationAmount = roundVoMoney(input.variationAmount);
  return {
    originalContractValue,
    variationAmount,
    revisedContractValue: roundVoMoney(originalContractValue + variationAmount),
  };
}

/** Presentation totals for Variation Order Summary (Original − Removed + Added). */
export function computeVoPresentationTotals(input: {
  originalContractValue: number;
  lines: Array<{ changeType: string; lineTotalDelta: number }>;
}): {
  originalContractValue: number;
  removedItemsTotal: number;
  addedItemsTotal: number;
  revisedContractTotal: number;
} {
  let removedItemsTotal = 0;
  let addedItemsTotal = 0;
  for (const l of input.lines) {
    const delta = Number(l.lineTotalDelta) || 0;
    if (l.changeType === "removed") {
      removedItemsTotal += Math.abs(delta);
      continue;
    }
    if (l.changeType === "added") {
      addedItemsTotal += Math.max(0, delta);
      continue;
    }
    if (l.changeType === "quantity_changed") {
      if (delta < 0) removedItemsTotal += Math.abs(delta);
      else if (delta > 0) addedItemsTotal += delta;
    }
  }
  const originalContractValue = roundVoMoney(input.originalContractValue);
  removedItemsTotal = roundVoMoney(removedItemsTotal);
  addedItemsTotal = roundVoMoney(addedItemsTotal);
  return {
    originalContractValue,
    removedItemsTotal,
    addedItemsTotal,
    revisedContractTotal: roundVoMoney(originalContractValue - removedItemsTotal + addedItemsTotal),
  };
}
